import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import classnames from 'classnames'
import type { IContainerHeaderProps } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type Conversation } from 'entities/Conversation'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { type EnumSyncType } from 'widgets/ContactsDetails'
import { type ProfilePageStore } from 'pages/conversations/pages/search/pages/profile/store/profilePageStore'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import styles from './styles.module.scss'

export class SectionContactDetailsStore {
  readonly contactsDetailsManageStore
  private _disposeConversation: IReactionDisposer | null = null
  private _disposeAlert: IReactionDisposer | null = null

  constructor(private _profilePageStore: ProfilePageStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
  }

  init = () => {
    this.reactionConversation()
    this.reactionAlert()
  }

  dispose = () => {
    this._disposeConversation?.()
    this._disposeAlert?.()
  }

  setAlertCreated = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const create_integration_contact = urlParams.get('create_integration_contact')

    if (create_integration_contact) {
      this.contactsDetailsManageStore.contactsDetailsStore?.setAlert({
        type: 'success',
        desc: 'Contact created',
      })

      urlParams.delete('create_integration_contact')

      uiStore.changeQueryRoute(urlParams.toString())
    }
  }

  reactionConversation = () => {
    this._disposeConversation?.()
    this._disposeConversation = reaction(
      () => this.conversation?.id,
      (new_id, old_id) => {
        if (typeof new_id !== 'number') return
        if (new_id === old_id) return
        if (!this.conversation) return

        this.setContactsDetails(this.conversation)

        this.setAlertCreated()
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionAlert = () => {
    this._disposeAlert?.()
    this._disposeAlert = reaction(
      () => this.contactsDetailsManageStore.contactsDetailsStore?.alert,
      (value) => {
        if (value) {
          this.setConfigContainerHeader({
            className: classnames(styles.header, styles.header__alert),
            height: 48,
            titleVariant: 'body-md-medium',
            paddingTop: 0,
            paddingBottom: 0,
          })
        } else {
          this.setConfigContainerHeader({
            className: styles.header,
            height: 48,
            titleVariant: 'body-md-medium',
          })
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  setConfigContainerHeader = (value: IContainerHeaderProps) => {
    this.contactsDetailsManageStore.contactsDetailsStore?.setConfigContainerHeaderProps(value)
  }

  setContactsDetails = (conversation: Conversation) => {
    this.contactsDetailsManageStore.setContactsDetails({
      conversationId: conversation.id,
      contactId: conversation.contact_id,
      variant: 'conversation',
      profileCardProps: {
        enableCall: true,
        onClickCall: () => {
          logV2SearchEvent(
            'BigConvSlideout_Call_Made',
            {
              event_id: 'bigconvslideout_message_sent',
              from: 'Search page (Contact panel)',
            },
            this._entities
          )
        },
      },
      containerHeaderProps: {
        className: styles.header,
        height: 48,
        titleVariant: 'body-md-medium',
      },
      variantPosition: 'fixed',
      onSync: (type) => {
        const sectionChanged = this._getTypeEvent(type)

        if (sectionChanged) {
          logV2SearchEvent(
            'BigConvSlideout_Contact_Details_Changed',
            {
              event_id: 'bigconvslideout_contact_details_changed',
              SectionChanged: sectionChanged,
            },
            this._entities
          )
        }
      },
    })
  }

  private _getTypeEvent = (type: EnumSyncType) => {
    if (type === 'conversation_info') return 'conversation assignee'
    if (type === 'contacts_info') return 'contact details'
    if (type === 'integration') return 'integration'
    if (type === 'notes') return 'notes'
    if (type === 'tags') return 'tags'

    return ''
  }

  get conversation() {
    return this.sectionConversationStore.conversation
  }

  get sectionConversationStore() {
    return this._profilePageStore.sectionConversationStore
  }

  get contactsDetailsStore() {
    return this.contactsDetailsManageStore.contactsDetailsStore
  }
}
